import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
} from "@/components/ui/sidebar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ChatSkeleton from "@/components/skeletons/chat-skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import NewChatsList from "./new-chats-list";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { JobAd } from "@/types/jobAdsTypes";
import { useState } from "react";
import useCreateJobAdSubCollectionDoc from "@/hooks/mutations/use-create-job-ad-sub-collection-doc";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import BulkMessageModal from "./bulk-message-modal";

interface ChatSidebarContentProps {
  isLoading: boolean;
  activeMatches: any[];
  archivedMatches: any[];
  selectedAd: JobAd | null;
  id: string | undefined;
}

export default function ChatSidebarContent({
  isLoading,
  activeMatches,
  archivedMatches,
  selectedAd,
  id,
}: ChatSidebarContentProps) {
  const [selectedChats, setSelectedChats] = useState<string[]>([]);
  const [isBulkMessageModalOpen, setIsBulkMessageModalOpen] = useState(false);

  const { mutateAsync: createJobAdSubCollectionDoc } =
    useCreateJobAdSubCollectionDoc();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleBulkEndProcess = async () => {
    if (!selectedAd?.id) return;
    try {
      await Promise.all(
        selectedChats.map((uid) =>
          createJobAdSubCollectionDoc({
            subCollection: "BusinessQuitProcessWithUser",
            jobAdId: selectedAd.id,
            candidateUid: uid,
          })
        )
      );
      toast({
        title: "Processerna har avslutats",
        description: `${selectedChats.length} processes ended.`,
      });
      setSelectedChats([]);
      queryClient.invalidateQueries({ queryKey: ["chat-matches", id] });
    } catch (e) {
      console.log(e);
      toast({
        title: "Något gick fel",
        description: "Kunde inte avsluta processerna.",
        variant: "destructive",
      });
    }
  };

  const selectAllChats = (checked: boolean | "indeterminate") => {
    if (checked === true) {
      setSelectedChats(activeMatches.map((match) => match.uid));
    } else setSelectedChats([]);
  };

  // Count matches marked for review
  const markedForReviewCount = activeMatches.filter(
    (match) => (match as any).reviewStatus === "marked_for_review"
  ).length;

  const allSelected =
    activeMatches.length > 0 && selectedChats.length === activeMatches.length;
  const someSelected =
    selectedChats.length > 0 && selectedChats.length < activeMatches.length;

  return (
    <SidebarContent>
      <BulkMessageModal
        open={isBulkMessageModalOpen}
        onOpenChange={setIsBulkMessageModalOpen}
        selectedChats={selectedChats}
        adId={selectedAd?.id || ""}
        onMessageSent={() => setSelectedChats([])}
        matches={[...activeMatches, ...archivedMatches]}
      />
      <SidebarGroup className="px-0">
        <SidebarGroupContent>
          {isLoading ? (
            <ChatSkeleton />
          ) : (
            <Tabs
              defaultValue="active"
              onValueChange={() => setSelectedChats([])}
            >
              <TabsList className="grid grid-cols-2 mx-3.5">
                <TabsTrigger value="active" className="relative">
                  Aktiva
                  {markedForReviewCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="ml-2 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs"
                    >
                      {markedForReviewCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="archived">Arkiverade</TabsTrigger>
              </TabsList>
              <div
                className="flex gap-x-0.5 border-b items-center
                mt-2 px-3.5 h-10"
              >
                <Checkbox
                  className="ml-0.5"
                  onCheckedChange={selectAllChats}
                  checked={
                    allSelected ? true : someSelected ? "indeterminate" : false
                  }
                />
                <div className="ml-2.5 mt-0.5 text-xs font-semibold text-gray-500">
                  {selectedChats.length
                    ? `${selectedChats.length} ${
                        selectedChats.length === 1 ? "vald" : "valda"
                      }`
                    : "Select all"}
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="ml-auto text-xs font-semibold text-gray-500"
                      disabled={!selectedChats.length}
                    >
                      Alternativ
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="start">
                    <DropdownMenuGroup>
                      <DropdownMenuItem
                        onSelect={() => setIsBulkMessageModalOpen(true)}
                      >
                        Skicka gruppmeddelande
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={handleBulkEndProcess}>
                        Avsluta process
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <TabsContent value="active">
                <NewChatsList
                  matches={activeMatches}
                  selectedAdId={selectedAd?.id || ""}
                  selectedChats={selectedChats}
                  setSelectedChats={setSelectedChats}
                />
              </TabsContent>
              <TabsContent value="archived">
                <NewChatsList
                  matches={archivedMatches}
                  selectedAdId={selectedAd?.id || ""}
                  selectedChats={selectedChats}
                  setSelectedChats={setSelectedChats}
                />
              </TabsContent>
            </Tabs>
          )}
        </SidebarGroupContent>
      </SidebarGroup>
    </SidebarContent>
  );
}
